import React, { useEffect, useMemo, useState } from 'react';
import { Star, MessageSquare, Search, AlertTriangle, Flag, EyeOff, Edit3, Trash2, Send, X, ChevronDown } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Rating } from '../ui/Rating';
import { Pagination } from '../ui/Pagination';
interface ReviewsTabProps {
  showToast: (msg: string) => void;
  setActiveTab?: (tab: string) => void;
}
type ReviewStatus = 'new' | 'published' | 'moderation';
type FilterType = 'all' | 'new' | 'no-reply' | 'low' | 'moderation';
interface Review {
  id: number;
  author: string;
  service: string;
  rating: number;
  text: string;
  date: string;
  status: ReviewStatus;
  reply: string;
}
const REPLY_TEMPLATES = ['Благодарю за обратную связь! Я обязательно учту ваши пожелания в дальнейшей работе.', 'Мне жаль, что консультация не оправдала ваших ожиданий. Давайте обсудим, как я могу это исправить.', 'Спасибо за ваш отзыв. Я ценю честность и готова разобраться в ситуации подробнее.'];
const INITIAL_REVIEWS: Review[] = [{
  id: 1,
  author: 'Елена В.',
  service: 'Натальная карта (Видео)',
  rating: 5,
  text: 'Потрясающая консультация! Анна очень подробно разобрала мою карту, ответила на все вопросы. Многое встало на свои места. Обязательно вернусь за соляром.',
  date: 'Вчера',
  status: 'published',
  reply: 'Елена, благодарю за теплые слова! Буду рада видеть вас снова.'
}, {
  id: 2,
  author: 'Дмитрий С.',
  service: 'Соляр на год',
  rating: 4,
  text: 'Хороший разбор, много полезной информации. Единственный минус — пришлось подождать ответа в чате чуть дольше заявленного.',
  date: '12 Окт 2024',
  status: 'published',
  reply: ''
}, {
  id: 3,
  author: 'Анна К.',
  service: 'Синастрия',
  rating: 5,
  text: 'Очень точный разбор отношений! Прям в точку про наши конфликты и как их сглаживать.',
  date: '10 Окт 2024',
  status: 'new',
  reply: ''
}, {
  id: 4,
  author: 'Игорь М.',
  service: 'Краткий ответ на вопрос',
  rating: 2,
  text: 'Ответ показался слишком общим, ожидал больше конкретики за эти деньги.',
  date: '05 Окт 2024',
  status: 'moderation',
  reply: ''
}, {
  id: 5,
  author: 'Мария Л.',
  service: 'Натальная карта',
  rating: 5,
  text: 'Замечательный астролог! Всё объяснила доступным языком, дала практические рекомендации.',
  date: '01 Окт 2024',
  status: 'published',
  reply: 'Мария, спасибо! Рада, что разбор оказался полезным.'
}, {
  id: 6,
  author: 'Сергей П.',
  service: 'Экспресс-консультация',
  rating: 3,
  text: 'Нормально, но хотелось бы больше деталей. Ответ был довольно краткий.',
  date: '28 Сен 2024',
  status: 'new',
  reply: ''
}, {
  id: 7,
  author: 'Ольга Т.',
  service: 'Синастрия',
  rating: 1,
  text: 'Совершенно не согласна с интерпретацией. Ощущение, что это шаблонный ответ.',
  date: '25 Сен 2024',
  status: 'published',
  reply: ''
}, {
  id: 8,
  author: 'Алина Р.',
  service: 'Соляр на год',
  rating: 5,
  text: 'Уже второй раз заказываю соляр. Всё сбывается! Очень благодарна.',
  date: '20 Сен 2024',
  status: 'published',
  reply: ''
}];
export function ReviewsTab({
  showToast,
  setActiveTab
}: ReviewsTabProps) {
  const [reviews, setReviews] = useState<Review[]>(INITIAL_REVIEWS);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyingToId, setReplyingToId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [editingReplyId, setEditingReplyId] = useState<number | null>(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  // Computed metrics
  const avgRating = (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);
  const totalReviews = reviews.length;
  const newCount = reviews.filter(r => r.status === 'new').length;
  const needsReplyCount = reviews.filter(r => !r.reply && r.status !== 'moderation').length;
  const lowRatingCount = reviews.filter(r => r.rating <= 3).length;
  // Filtered reviews
  const filtered = useMemo(() => {
    return reviews.filter(r => {
      const matchesFilter = filter === 'all' || filter === 'new' && r.status === 'new' || filter === 'no-reply' && !r.reply && r.status !== 'moderation' || filter === 'low' && r.rating <= 3 || filter === 'moderation' && r.status === 'moderation';
      const matchesSearch = !searchQuery || r.author.toLowerCase().includes(searchQuery.toLowerCase()) || r.service.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [reviews, filter, searchQuery]);
  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedReviews = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Handlers
  const handleSendReply = (reviewId: number) => {
    if (!replyText.trim()) {
      showToast('Введите текст ответа');
      return;
    }
    setReviews(prev => prev.map(r => r.id === reviewId ? {
      ...r,
      reply: replyText.trim(),
      status: r.status === 'new' ? 'published' : r.status
    } : r));
    setReplyingToId(null);
    setReplyText('');
    setShowTemplates(false);
    showToast('Ответ опубликован');
    console.log(`[Admin Sync] Reply sent for review #${reviewId}`);
  };
  const handleEditReply = (reviewId: number) => {
    if (!editReplyText.trim()) {
      showToast('Введите текст ответа');
      return;
    }
    setReviews(prev => prev.map(r => r.id === reviewId ? {
      ...r,
      reply: editReplyText.trim()
    } : r));
    setEditingReplyId(null);
    setEditReplyText('');
    showToast('Ответ обновлён');
    console.log(`[Admin Sync] Reply edited for review #${reviewId}`);
  };
  const handleDeleteReply = (reviewId: number) => {
    setReviews(prev => prev.map(r => r.id === reviewId ? {
      ...r,
      reply: ''
    } : r));
    showToast('Ответ удалён');
    console.log(`[Admin Sync] Reply deleted for review #${reviewId}`);
  };
  const handleReport = (reviewId: number) => {
    showToast('Жалоба отправлена на рассмотрение');
    console.log(`[Admin Sync] Review #${reviewId} reported`);
  };
  const handleHide = (reviewId: number) => {
    setReviews(prev => prev.map(r => r.id === reviewId ? {
      ...r,
      status: 'moderation' as ReviewStatus
    } : r));
    showToast('Отзыв отправлен на модерацию');
    console.log(`[Admin Sync] Review #${reviewId} sent to moderation`);
  };
  const handleCardClick = (newFilter: FilterType) => {
    setFilter(newFilter);
    setSearchQuery('');
  };
  const filterTabs: {
    key: FilterType;
    label: string;
    count?: number;
  }[] = [{
    key: 'all',
    label: 'Все'
  }, {
    key: 'new',
    label: 'Новые',
    count: newCount
  }, {
    key: 'no-reply',
    label: 'Без ответа',
    count: needsReplyCount
  }, {
    key: 'low',
    label: 'Низкая оценка',
    count: lowRatingCount
  }, {
    key: 'moderation',
    label: 'На модерации'
  }];
  const renderStars = (rating: number) => {
    return <div className="flex items-center gap-0.5" data-id="element-14905">
        {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-4 h-4 ${s <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-14906" />)}
      </div>;
  };
  return <div className="space-y-6" data-id="element-14907">
      {/* Header */}
      <div data-id="element-14908">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-14909">Отзывы</h2>
        <p className="text-slate-500 text-sm mt-1" data-id="element-14910">
          Управление отзывами клиентов
        </p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-14911">
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => handleCardClick('all')} data-id="element-14912">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14913">
            Средний рейтинг
          </div>
          <div className="flex items-center gap-2" data-id="element-14914">
            <span className="text-2xl font-bold text-slate-900" data-id="element-14915">
              {avgRating}
            </span>
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" data-id="element-14916" />
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => handleCardClick('all')} data-id="element-14917">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14918">
            Всего отзывов
          </div>
          <div className="text-2xl font-bold text-slate-900" data-id="element-14919">
            {totalReviews}
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => handleCardClick('new')} data-id="element-14920">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14921">Новых</div>
          <div className="text-2xl font-bold text-indigo-600" data-id="element-14922">{newCount}</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => handleCardClick('no-reply')} data-id="element-14923">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14924">
            Без ответа
          </div>
          <div className="text-2xl font-bold text-amber-600" data-id="element-14925">
            {needsReplyCount}
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-rose-50/50 border-rose-200" onClick={() => handleCardClick('low')} data-id="element-14926">
          <div className="text-xs text-rose-600 mb-1 font-medium" data-id="element-14927">
            Низкая оценка (1-3)
          </div>
          <div className="text-2xl font-bold text-rose-600" data-id="element-14928">
            {lowRatingCount}
          </div>
        </Card>
      </div>

      {/* Filters + Search */}
      <Card padding="none" className="overflow-hidden" data-id="element-14929">
        <div className="p-4 border-b border-slate-100" data-id="element-14930">
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-14931">
            {/* Filter Tabs */}
            <div className="flex bg-slate-100 p-1 rounded-lg overflow-x-auto flex-1" data-id="element-14932">
              {filterTabs.map(tab => <button key={tab.key} onClick={() => setFilter(tab.key)} className={`px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 ${filter === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-14933">
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${filter === tab.key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-14934">
                      {tab.count}
                    </span>}
                </button>)}
            </div>

            {/* Search */}
            <div className="relative sm:w-64" data-id="element-14935">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-14936" />
              <input type="text" placeholder="Имя клиента или услуга..." className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-14937" />
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="divide-y divide-slate-100" data-id="element-14938">
          {paginatedReviews.length > 0 ? paginatedReviews.map(r => {
          const isLow = r.rating <= 3;
          const isReplying = replyingToId === r.id;
          const isEditing = editingReplyId === r.id;
          return <div key={r.id} className={`p-4 sm:p-5 ${isLow && !r.reply ? 'bg-rose-50/30' : ''}`} data-id="element-14939">
                  {/* Review Header */}
                  <div className="flex justify-between items-start mb-3" data-id="element-14940">
                    <div className="flex items-center gap-3" data-id="element-14941">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 text-sm shrink-0" data-id="element-14942">
                        {r.author.charAt(0)}
                      </div>
                      <div data-id="element-14943">
                        <div className="font-bold text-slate-900 text-sm" data-id="element-14944">
                          {r.author}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-14945">
                          {r.date} · {r.service}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0" data-id="element-14946">
                      {r.status === 'new' && <Badge variant="success" size="sm" data-id="element-14947">
                          Новый
                        </Badge>}
                      {r.status === 'moderation' && <Badge variant="warning" size="sm" data-id="element-14948">
                          На модерации
                        </Badge>}
                      {r.reply && r.status !== 'moderation' && <Badge variant="neutral" size="sm" data-id="element-14949">
                          Отвечен
                        </Badge>}
                    </div>
                  </div>

                  {/* Rating + Text */}
                  <div className="mb-3" data-id="element-14950">
                    {renderStars(r.rating)}
                    <p className="text-sm text-slate-700 mt-2 leading-relaxed" data-id="element-14951">
                      {r.text}
                    </p>
                  </div>

                  {/* Low Rating Warning */}
                  {isLow && !r.reply && r.status !== 'moderation' && <div className="flex items-center gap-2 mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-14952">
                      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" data-id="element-14953" />
                      <span className="text-xs text-amber-700 font-medium" data-id="element-14954">
                        Рекомендуем ответить на этот отзыв
                      </span>
                    </div>}

                  {/* Existing Reply */}
                  {r.reply && !isEditing && <div className="bg-slate-50 rounded-lg p-3 ml-4 border-l-2 border-indigo-200 mb-3" data-id="element-14955">
                      <div className="flex justify-between items-start" data-id="element-14956">
                        <div className="text-xs font-bold text-slate-900 mb-1" data-id="element-14957">
                          Ваш ответ:
                        </div>
                        <div className="flex gap-1" data-id="element-14958">
                          <button onClick={() => {
                    setEditingReplyId(r.id);
                    setEditReplyText(r.reply);
                  }} className="p-1 text-slate-400 hover:text-indigo-600 transition-colors" title="Редактировать" data-id="element-14959">
                            <Edit3 className="w-3.5 h-3.5" data-id="element-14960" />
                          </button>
                          <button onClick={() => handleDeleteReply(r.id)} className="p-1 text-slate-400 hover:text-rose-600 transition-colors" title="Удалить" data-id="element-14961">
                            <Trash2 className="w-3.5 h-3.5" data-id="element-14962" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600" data-id="element-14963">{r.reply}</p>
                    </div>}

                  {/* Edit Reply Form */}
                  {isEditing && <div className="ml-4 mb-3 animate-in fade-in slide-in-from-top-2" data-id="element-14964">
                      <textarea className="w-full p-3 border border-indigo-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none bg-white" rows={3} value={editReplyText} onChange={e => setEditReplyText(e.target.value)} placeholder="Редактируйте ответ..." data-id="element-14965" />
                      <div className="flex gap-2 mt-2 justify-end" data-id="element-14966">
                        <Button variant="ghost" size="sm" onClick={() => {
                  setEditingReplyId(null);
                  setEditReplyText('');
                }} data-id="element-14967">
                          Отмена
                        </Button>
                        <Button variant="primary" size="sm" icon={<Send className="w-3.5 h-3.5" data-id="element-14969" />} onClick={() => handleEditReply(r.id)} data-id="element-14968">
                          Сохранить
                        </Button>
                      </div>
                    </div>}

                  {/* Reply Form */}
                  {isReplying && <div className="mt-3 pt-3 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-14970">
                      {/* Templates for low ratings */}
                      {isLow && <div className="mb-3" data-id="element-14971">
                          <button onClick={() => setShowTemplates(!showTemplates)} className="text-xs text-indigo-600 font-medium flex items-center gap-1 hover:text-indigo-700 mb-2" data-id="element-14972">
                            Шаблоны ответов{' '}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showTemplates ? 'rotate-180' : ''}`} data-id="element-14973" />
                          </button>
                          {showTemplates && <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2" data-id="element-14974">
                              {REPLY_TEMPLATES.map((tpl, i) => <button key={i} onClick={() => {
                    setReplyText(tpl);
                    setShowTemplates(false);
                  }} className="block w-full text-left text-xs text-slate-600 p-2 rounded-lg bg-slate-50 hover:bg-indigo-50 hover:text-indigo-700 border border-slate-100 transition-colors" data-id="element-14975">
                                  {tpl}
                                </button>)}
                            </div>}
                        </div>}

                      <textarea className="w-full p-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" rows={3} value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Напишите ответ клиенту..." autoFocus data-id="element-14976" />
                      <div className="flex gap-2 mt-2 justify-end" data-id="element-14977">
                        <Button variant="ghost" size="sm" onClick={() => {
                  setReplyingToId(null);
                  setReplyText('');
                  setShowTemplates(false);
                }} data-id="element-14978">
                          Отмена
                        </Button>
                        <Button variant="primary" size="sm" icon={<Send className="w-3.5 h-3.5" data-id="element-14980" />} onClick={() => handleSendReply(r.id)} data-id="element-14979">
                          Отправить
                        </Button>
                      </div>
                    </div>}

                  {/* Action Buttons */}
                  {!isReplying && !isEditing && <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100" data-id="element-14981">
                      {!r.reply && r.status !== 'moderation' && <Button variant={isLow ? 'primary' : 'outline'} size="sm" icon={<MessageSquare className="w-3.5 h-3.5" data-id="element-14983" />} onClick={() => {
                setReplyingToId(r.id);
                setReplyText('');
                setEditingReplyId(null);
              }} data-id="element-14982">
                          {isLow ? 'Быстрый ответ' : 'Ответить'}
                        </Button>}
                      {r.status === 'moderation' && <Button variant="outline" size="sm" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => {
                showToast('Отзыв отправлен на повторную проверку');
                console.log(`[Admin Sync] Review #${r.id} re-submitted for moderation`);
              }} data-id="element-14984">
                          Отправить на проверку
                        </Button>}
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-rose-600" icon={<Flag className="w-3.5 h-3.5" data-id="element-14986" />} onClick={() => handleReport(r.id)} data-id="element-14985">
                        Жалоба
                      </Button>
                      {r.status !== 'moderation' && <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600" icon={<EyeOff className="w-3.5 h-3.5" data-id="element-14988" />} onClick={() => handleHide(r.id)} data-id="element-14987">
                          Скрыть
                        </Button>}
                    </div>}
                </div>;
        }) : <div className="p-12 text-center text-slate-500" data-id="element-14989">
              <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-14990" />
              <div className="text-base font-medium text-slate-900 mb-1" data-id="element-14991">
                Отзывы не найдены
              </div>
              <p className="text-sm" data-id="element-14992">
                Попробуйте изменить фильтр или поисковый запрос
              </p>
            </div>}
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-14993">
            <span className="text-xs text-slate-500" data-id="element-14994">
              Показано {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} из{' '}
              {filtered.length}
            </span>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-14995" />
          </div>}
      </Card>
    </div>;
}